import { z } from 'zod';
import { VENDOR_CREDIT_TXN_KINDS, VENDOR_RETURN_OUTCOMES, VENDOR_RETURN_STATUSES, } from '../constants/vendor-returns.js';
import { moneyCentsSchema, uuidSchema } from './common.js';
/**
 * Open a return for damaged goods bought from a vendor (t1154). Either one
 * tracked device (`unitId`), or a quantity of counted stock taken off the
 * shelf at `locationId`. `sourceCostId` ties it to the purchase line it came
 * from, which supplies the invoice reference and the default credit amount.
 */
export const createVendorReturnSchema = z
    .object({
    vendorId: uuidSchema,
    unitId: uuidSchema.nullable().default(null),
    productId: uuidSchema.nullable().default(null),
    locationId: uuidSchema.nullable().default(null),
    qty: z.number().int().positive().max(1000).default(1),
    sourceCostId: uuidSchema.nullable().default(null),
    invoiceRef: z.string().trim().max(64).nullable().default(null),
    reason: z.string().trim().min(1, 'Say what is wrong with it').max(500),
    /** Defaults to what the item cost when left out. */
    expectedCreditCents: moneyCentsSchema.nullable().default(null),
})
    .refine((v) => v.unitId !== null || (v.productId !== null && v.locationId !== null), {
    message: 'Choose a device, or a product and where it is',
    path: ['unitId'],
})
    .refine((v) => v.unitId === null || v.qty === 1, {
    message: 'A device is returned one at a time',
    path: ['qty'],
});
export const shipVendorReturnSchema = z.object({
    trackingNumber: z.string().trim().max(120).nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
});
/**
 * The vendor confirmed and refunded. `amountCents` is what they actually
 * gave, which may be less than expected (a partial refund) — the shortfall
 * stays visible on the return.
 */
export const creditVendorReturnSchema = z.object({
    outcome: z.enum(VENDOR_RETURN_OUTCOMES),
    amountCents: moneyCentsSchema,
    note: z.string().trim().max(500).nullable().default(null),
});
export const vendorReturnSchema = z.object({
    id: uuidSchema,
    vendorId: uuidSchema,
    vendorName: z.string(),
    unitId: uuidSchema.nullable(),
    unitImei: z.string().nullable(),
    productId: uuidSchema,
    productName: z.string(),
    locationId: uuidSchema.nullable(),
    locationName: z.string().nullable(),
    qty: z.number().int(),
    sourceCostId: uuidSchema.nullable(),
    invoiceRef: z.string().nullable(),
    reason: z.string(),
    status: z.enum(VENDOR_RETURN_STATUSES),
    expectedCreditCents: z.number().int(),
    creditedCents: z.number().int().nullable(),
    outcome: z.enum(VENDOR_RETURN_OUTCOMES).nullable(),
    trackingNumber: z.string().nullable(),
    note: z.string().nullable(),
    shippedAt: z.iso.datetime().nullable(),
    vendorReceivedAt: z.iso.datetime().nullable(),
    creditedAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
export const vendorReturnListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    status: z.enum(VENDOR_RETURN_STATUSES).optional(),
    /** `open` = every status that is not yet credited or cancelled. */
    open: z.enum(['true', 'false']).optional(),
    vendorId: uuidSchema.optional(),
});
export const vendorCreditTransactionSchema = z.object({
    id: uuidSchema,
    kind: z.enum(VENDOR_CREDIT_TXN_KINDS),
    amountCents: z.number().int(),
    balanceAfterCents: z.number().int(),
    vendorReturnId: uuidSchema.nullable(),
    invoiceRef: z.string().nullable(),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const vendorCreditSchema = z.object({
    vendorId: uuidSchema,
    balanceCents: z.number().int(),
    transactions: z.array(vendorCreditTransactionSchema),
});
/** Put some of the vendor's credit toward an invoice. */
export const applyVendorCreditSchema = z.object({
    amountCents: moneyCentsSchema.refine((n) => n > 0, 'Enter an amount'),
    invoiceRef: z.string().trim().min(1, 'Enter the invoice reference').max(64),
    note: z.string().trim().max(500).nullable().default(null),
});
export const invoiceQuerySchema = z.object({
    vendor: z.string().trim().min(1).max(120),
    invoiceRef: z.string().trim().min(1).max(64),
});
/**
 * One supplier invoice as it stands after returns: the purchase lines, the
 * returns raised against them, and what is still owed once returned goods
 * and credit applied are taken off.
 */
export const invoiceSummarySchema = z.object({
    vendor: z.string(),
    invoiceRef: z.string(),
    lines: z.array(z.object({
        costId: uuidSchema,
        label: z.string(),
        unitImei: z.string().nullable(),
        amountCents: z.number().int(),
        paymentStatus: z.string(),
        returnId: uuidSchema.nullable(),
        returnStatus: z.enum(VENDOR_RETURN_STATUSES).nullable(),
    })),
    returns: z.array(vendorReturnSchema),
    creditApplied: z.array(vendorCreditTransactionSchema),
    totalCents: z.number().int(),
    /** Lines already marked paid. */
    paidCents: z.number().int(),
    /** Expected credit for items sent back and not yet credited, plus what was credited. */
    returnedCents: z.number().int(),
    creditAppliedCents: z.number().int(),
    netOwedCents: z.number().int(),
});
//# sourceMappingURL=vendor-returns.js.map